import { Component, forwardRef, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild } from '@angular/core';

import { AbstractFormFieldComponent } from '../../../PageBuilder/components/FormField/abstract-form-field.component';
import { DateRangeFieldModel } from '../../models/date-range-field.model';
import { DateQueryModel } from '../../../Forms/models/date-query.model';
import { ReportSeriesModel } from '../../models/report-series.model';
import { ReportDateRangeModel } from '../../models/report-date-range.model';
import { DropdownOptionModel } from '../../../Forms/models/dropdown-option.model';
import {
    getMonitoringDateQueryInputFolderOptions,
    getMonitoringDateQueryInputTopicOptions,
    monitoringDefaultDateTopicOptions
} from '../../../../Monitor/Shared/models/monitoring-date-query-input-options';
import { filter, Subscription } from 'rxjs';
import { SearchRangeModel } from '../../../../Monitor/Core/models/search-range.model';
import { MonitorConfigService } from '../../../MonitorShared/services/monitor-config.service';
import { dateQueryInput24HoursOption } from '../../../Forms/models/date-query-input-options.model';
import { GoogleAnalyticsChartTypeEnum } from '../../../../api/externalData/enums/google-analytics-chart-types.enum';
import { DateQueryTypeEnum } from '../../../../api/mentions/enum/date-query-type.enum';
import { ReportBlockTypeEnum } from '../../enums/report-block-type.enum';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { DateQueryPopupComponent } from '../../../Forms/components/DateQueryPopup/date-query-popup.component';
import { defaultDateTypeOptions, savedSourceDateTypeOptions } from '../../../Forms/components/DateRangeSelector/date-range-selector.component';
import { PreferencesModel } from '../../../../Core/Users/models/preferences.model';
import { reportSavedSourceTypes } from '../SeriesPickerField/series-picker-field.component';
import { DateQueryDateTypeEnum } from '../../../../api/mentions/enum/date-query-date-type.enum';
import { NeoFacetDateFieldApiEnum } from '../../../../api/mentions/model/neo-facet-date-field-api.enum';
import { RelativeTimezoneDateTypeEnum } from '@api/mentions/enum/relative-timezone-date-type.enum';


@Component({
    selector: 'agpr-date-range-field',
    templateUrl: './date-range-field.component.html',
    styleUrls: ['./date-range-field.component.scss'],
    providers: [
        {
            provide: AbstractFormFieldComponent,
            useExisting: forwardRef(() => DateRangeFieldComponent)
        }
    ],
    standalone: false
})
export class DateRangeFieldComponent extends AbstractFormFieldComponent implements OnDestroy, OnChanges {
    @Input() declare field: DateRangeFieldModel;
    @Input() declare value: ReportSeriesModel[];
    @Input() dateTypeOptions:  DropdownOptionModel[] = defaultDateTypeOptions;

    @ViewChild('dateQueryPopup') dateQueryPopup: DateQueryPopupComponent;

    dateFilterOptions: DropdownOptionModel[] = getMonitoringDateQueryInputFolderOptions();
    hideDateTypeSelection: boolean = false;

    dateRange: DateQueryModel;
    seriesList: ReportSeriesModel[] = [];
    showAllOption: boolean = true;
    showPicker: boolean = true;

    subscriptions: Subscription[] = [];
    searchRange: SearchRangeModel = null;
    gaChartType = GoogleAnalyticsChartTypeEnum;

    private preferences: PreferencesModel;
    readonly googleAnalyticsChartTypes: string[] = [
        ReportBlockTypeEnum.TOP_SOCIAL_REFERRERS, ReportBlockTypeEnum.TOP_REFERRAL_SOURCES, ReportBlockTypeEnum.REFERRAL_TREND
    ];

    constructor(
        private monitorConfigService: MonitorConfigService,
        public currentUserStateService: CurrentUserStateService
    ) {
        super();
        this.subscriptions.push(
            this.monitorConfigService.getSearchRange().subscribe(
                searchRange => {
                    this.searchRange = searchRange;
                    this.setMinimumSearchRange();
                }
            ),
            this.currentUserStateService.getPreferences().pipe(filter(x => !!x)).subscribe(prefs => {
                this.preferences = prefs;
            })
        );
        this.hideDateTypeSelection = this.googleAnalyticsChartTypes.includes(this.block?.type?.key);
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.hasOwnProperty('value') || changes.hasOwnProperty('dateTypeOptions')) {
            if (this.value && this.value.length > 0) {
                this.seriesList = this.value;
            } else {
                this.seriesList = this.getDefaultSeriesList();
            }
            let showAll = this.field.allowAllOption;
            if (this.seriesList && this.seriesList.length > 0) {
                // backwards compatibility layer for old relative time
                // if date query is 'last 24 hours', 'last 3 days', 'last 7 days', 'last 30 days', convert it to the correct relative timezone query
                if (
                    this.seriesList[0].dateRange?.relativeStartOffsetH === -24
                ||  this.seriesList[0].dateRange?.relativeStartOffsetH === -72
                ||   this.seriesList[0].dateRange?.relativeStartOffsetH === -168
                ||   this.seriesList[0].dateRange?.relativeStartOffsetH === -720
                ) {
                    let relativeTimezoneQuery;
                    switch (this.seriesList[0].dateRange.relativeStartOffsetH) {
                        case -24:
                            relativeTimezoneQuery = DateQueryModel.getRelativeTimezoneQuery(RelativeTimezoneDateTypeEnum.LAST_24_HOURS);
                            break;
                        case -72:
                            relativeTimezoneQuery = DateQueryModel.getRelativeTimezoneQuery(RelativeTimezoneDateTypeEnum.LAST_3_DAYS);
                            break;
                        case -168:
                            relativeTimezoneQuery = DateQueryModel.getRelativeTimezoneQuery(RelativeTimezoneDateTypeEnum.LAST_7_DAYS);
                            break;
                        case -720:
                            relativeTimezoneQuery = DateQueryModel.getRelativeTimezoneQuery(RelativeTimezoneDateTypeEnum.LAST_30_DAYS);
                            break;
                    }
                    relativeTimezoneQuery.dateType = this.seriesList[0].dateRange?.dateType;
                    this.seriesList[0].dateRange = new ReportDateRangeModel().setFromDateQuery(relativeTimezoneQuery);
                }
                // if date range dropdown options do not contain the initial date range, setting it to dateQueryInput24HoursOption
                if (this.seriesList[0].dateRange?.type === DateQueryTypeEnum.RELATIVE
                    && !this.dateFilterOptions.find(option => {
                        return option.value.query.relativeStartOffsetH === this.seriesList[0].dateRange.relativeStartOffsetH;
                    })) {
                    this.seriesList[0].dateRange = new ReportDateRangeModel().setFromDateQuery(dateQueryInput24HoursOption.value.query);
                }

                this.dateRange = this.seriesList[0].dateRange;

                if (this.dateRange.type === DateQueryTypeEnum.RELATIVE_TIMEZONE) {
                    this.dateRange.timezone = this.preferences.timeZone;
                }

                showAll = !this.seriesList.find(seriesItem => !seriesItem.canUseAllDateRange());
            }

            if (this.block?.type?.key === ReportBlockTypeEnum.RECENT_TOPICS) {
                this.dateFilterOptions = monitoringDefaultDateTopicOptions;
            } else {
                this.setShowAllOption(showAll);
            }

            // set date type options based on selected series types
            const areAllSavedSources = this.seriesList.every(series => reportSavedSourceTypes.includes(series.type));
            this.dateTypeOptions = areAllSavedSources ? savedSourceDateTypeOptions : defaultDateTypeOptions;

            this.setMinimumSearchRange();

            if (this.field.appearCondition) {
                this.showPicker = this.field.appearCondition(this.block.displayElement);
            }
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    /**
     * Updates the date range.
     *
     * @param dateRange
     *
     * @returns void
     */
    dateRangeChanged(dateRange: DateQueryModel): void {
        this.seriesList.forEach(series => {
            series.dateRange = new ReportDateRangeModel().setFromDateQuery(this.dateRange);
            if (series.mentions.neoFacets) {
                series.mentions.neoFacets.forEach(facet => {
                    if (facet.dateField) {
                        if (this.dateRange.dateType === DateQueryDateTypeEnum.PUBLISHED) {
                            facet.dateField = NeoFacetDateFieldApiEnum.CREATED_AT;
                        } else if (this.dateRange.dateType === DateQueryDateTypeEnum.HARVESTED) {
                            facet.dateField = NeoFacetDateFieldApiEnum.TIMESTAMP;
                        }
                    }
                });
            }
        });

        this.changes.emit(this.seriesList.slice());
    }

    /**
     * Sets the date options based on the seriesType
     */
    setShowAllOption(showAll: boolean) {
        if (showAll) {
            this.dateFilterOptions = getMonitoringDateQueryInputFolderOptions();
        } else {
            this.dateFilterOptions = getMonitoringDateQueryInputTopicOptions();
        }

        this.showAllOption = showAll;
    }

    /**
     * Returns a new ReportSeriesModel[] with default values
     *
     * @returns ReportSeriesModel[]
     */
    getDefaultSeriesList(): ReportSeriesModel[] {
        let blockTypeKey = null;

        if (this.block && this.block.type && this.block.type.key) {
            blockTypeKey = this.block.type.key;
        }

        return [new ReportSeriesModel(null, blockTypeKey)];
    }

    /**
     * If the appropriate data is set, make sure that topics don't go outside their allowed range
     */
    private setMinimumSearchRange(): void {
        if (
            !this.seriesList ||
            !this.seriesList.length ||
            !this.searchRange ||
            // skip it if we use saved source
            this.showAllOption ||
            // skip it if we use ga charts
            (this.block && this.gaChartType[this.block.type.key]) ||
            !this.seriesList[0].dateRange
        ) {
            return;
        }

        // since this.dateRange = this.seriesList[0].dateRange above, change on dateRange will change this.dateRange as well
        const dateRange = this.seriesList[0].dateRange;

        if (dateRange.absoluteRange) {
            if (dateRange.absoluteRange.end < this.searchRange.from) {
                dateRange.absoluteRange = null;
                dateRange.absoluteDate = this.searchRange.from;
            } else if (dateRange.absoluteRange.start < this.searchRange.from) {
                dateRange.absoluteRange.start = this.searchRange.from;
            }
        }

        if (dateRange.absoluteDate && dateRange.absoluteDate < this.searchRange.from) {
            dateRange.absoluteDate = this.searchRange.from;
        }

        // Force it so that the displayValue gets updated
        this.dateQueryPopup?.writeValue(this.dateRange);

    }
}

